import cn from 'clsx';
import { AnimatePresence } from 'framer-motion';
import { FC, HTMLAttributes } from 'react';
import { createPortal } from 'react-dom';

import usePortal from '../../hooks/dialog/usePortal';

import s from './Toast.module.scss';
import { CreateToastOptions, ToastOptions } from './Toast.types';
import { ToastContext } from './ToastContext';
import ToastWrapper from './ToastWrapper';
import { useToastList } from './useToastList';

type NativeAttrs = Omit<HTMLAttributes<unknown>, keyof ToastOptions>;

export type ToastProviderProps = CreateToastOptions & NativeAttrs;

export const ToastProvider: FC<React.PropsWithChildren<React.PropsWithChildren<ToastProviderProps>>> = ({
  open = false,
  animated = true,
  children,
  className = '',
  containerClassName = '',
  ...restProps
}) => {
  const defaultToastOptions = { open, animated, ...restProps } as CreateToastOptions;
  const portal = usePortal('toast');

  const { toastConfig, toastList } = useToastList({
    ...defaultToastOptions,
    className: defaultToastOptions.className ? defaultToastOptions.className : className,
    containerClassName: defaultToastOptions.containerClassName ? defaultToastOptions.containerClassName : containerClassName
  });

  if (!portal) return null;
  const toastPortal = createPortal(
    <div className={cn(s['toast-container'])}>
      <AnimatePresence initial={false}>
        {toastList.map((toast) => (
          <ToastWrapper key={toast.id} {...toast} />
        ))}
      </AnimatePresence>
    </div>,
    portal
  );

  const ToastContextProvider = ToastContext.Provider;

  return (
    <ToastContextProvider value={toastConfig}>
      {children}
      {toastPortal}
    </ToastContextProvider>
  );
};
